import { useSyncExternalStore } from "react";

// Settings' Appearance, like the iPhone app's (StashItCore/AppAppearance.swift): dark unless you
// choose otherwise, since the walnut list is the one Mike picked. Kept in this browser's
// localStorage. index.html reads the same key before the app loads, so there's no flash of the
// wrong colours.

export type Appearance = "dark" | "light" | "system";
export type Scheme = "dark" | "light";

export const appearanceKey = "stashit.appearance";
const defaultAppearance: Appearance = "dark";

export const appearanceLabels: Record<Appearance, string> = {
  dark: "Dark",
  light: "Light",
  system: "System",
};

const isAppearance = (value: unknown): value is Appearance =>
  value == "dark" || value == "light" || value == "system";

const readStored = (): Appearance => {
  try {
    const value = localStorage.getItem(appearanceKey);
    return isAppearance(value) ? value : defaultAppearance;
  } catch {
    return defaultAppearance;
  }
};

const systemQuery = () => window.matchMedia("(prefers-color-scheme: dark)");

const listeners = new Set<() => void>();
const notify = () => listeners.forEach((listener) => listener());

let appearance = readStored();

export const resolveScheme = (value: Appearance): Scheme => {
  if (value != "system") return value;
  return systemQuery().matches ? "dark" : "light";
};

const applyToDocument = () => {
  const scheme = resolveScheme(appearance);
  const root = document.documentElement;
  root.classList.toggle("dark", scheme == "dark");
  root.classList.toggle("light", scheme == "light");
  document
    .querySelector('meta[name="theme-color"]')
    ?.setAttribute("content", scheme == "dark" ? "#1c140f" : "#efe0c3");
};

export const setAppearance = (value: Appearance) => {
  appearance = value;
  try {
    localStorage.setItem(appearanceKey, value);
  } catch {
    // Private windows can refuse; it still applies until the tab closes
  }
  applyToDocument();
  notify();
};

const subscribe = (listener: () => void) => {
  listeners.add(listener);
  // "Match system" follows the OS as it changes; another tab changing the setting does too
  const query = systemQuery();
  const onSystem = () => {
    applyToDocument();
    listener();
  };
  const onStorage = (event: StorageEvent) => {
    if (event.key != appearanceKey) return;
    appearance = readStored();
    applyToDocument();
    listener();
  };
  query.addEventListener("change", onSystem);
  window.addEventListener("storage", onStorage);
  return () => {
    listeners.delete(listener);
    query.removeEventListener("change", onSystem);
    window.removeEventListener("storage", onStorage);
  };
};

/** What's chosen, and the scheme it comes to right now. */
export const useAppearance = () => {
  const chosen = useSyncExternalStore(subscribe, () => appearance);
  const scheme = useSyncExternalStore(subscribe, () => resolveScheme(appearance));
  return { appearance: chosen, scheme, setAppearance };
};

applyToDocument();
